import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Field, Formik } from "formik";
import queryString from "query-string";
import React from "react";
import { Alert, Button as BSButton, Col, Form, FormGroup, InputGroup, Row, FormText } from "react-bootstrap";
import { connect } from "react-redux";
import { RouteComponentProps, useLocation, withRouter } from "react-router-dom";
import * as Yup from "yup";
import { logUserIn } from "../../../auth";
import { doSignup, trackSignup } from "../../../docuclipper/api";
import { logErrorToGA } from "../../../docuclipper/Logger";
import { createAlert, CreateAlertFn } from "../../../redux/reducers/Alerts";
import {
  formikInput,
  goToFacebook,
  goToGoogle,
  goToMicrosoft,
  sendGAEvent,
} from "../../../utils/utils";
import { capturePostHogEvent } from "../../../utils/posthog";
import { Btn } from "../../../shared/Btn";
import AlertBanner from "../../Docuclipper/AlertBanner/AlertBanner";
import ContinueWithSocial from "../Login/ContinueWithSocial";
import Logo from "../Login/Logo";
import { Helmet } from "react-helmet";

const COMMON_EMAIL_DOMAINS = [
  "gmail.com", "yahoo.com", "outlook.com", "hotmail.com", "icloud.com",
  "live.com", "me.com", "googlemail.com", "protonmail.com", "aol.com",
  "msn.com", "mac.com", "ymail.com", "yahoo.co.uk", "yahoo.com.au",
];

function levenshtein(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, (_, i) => [i]);
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1]
        : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    }
  }
  return dp[m][n];
}

function suggestEmailDomain(email: string): string | null {
  const at = email.lastIndexOf("@");
  if (at === -1 || at === email.length - 1) return null;
  const typed = email.slice(at + 1).toLowerCase();
  if (COMMON_EMAIL_DOMAINS.includes(typed)) return null;
  let best: string | null = null;
  let bestDist = 3;
  for (const domain of COMMON_EMAIL_DOMAINS) {
    const dist = levenshtein(typed, domain);
    if (dist < bestDist) { bestDist = dist; best = domain; }
  }
  return best;
}

type SignupFieldsProps = {
  onClick: (fields: any) => any;
  btnCta: string;
  loading: boolean;
  email?: string | null;
};

export const SignupFields: React.FC<SignupFieldsProps> = ({
  onClick,
  btnCta,
  loading,
  email,
}) => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const guestToken = queryParams.get("guestToken");
  const [emailSuggestion, setEmailSuggestion] = React.useState<string | null>(null);
  const [showPassword, setShowPassword] = React.useState(false);

  return (
    <Formik
      initialValues={{
        email: email || "",
        password: "",
        firstName: "",
        lastName: "",
        phoneNumber: "",
        companyName: "",
        jobTitle: "",
        guestToken,
      }}
      validationSchema={Yup.object().shape({
        email: Yup.string()
          .required("Email is required")
          .email("Invalid email"),
        password: Yup.string()
          .required("Password is required")
          .min(8, "Password is too short"),
        firstName: Yup.string() /*.required("First name is required")*/,
        lastName: Yup.string() /*.required("Last name is required")*/,
        phoneNumber: Yup.string(),
      })}
      onSubmit={(fields) => {
        onClick(fields);
      }}
      render={({ errors, status, isValid, submitForm, values, setFieldValue }) => {
        const onClick = () => {
          submitForm();
        };
        return (
          <>
            <Helmet>
              <title>Create your DocuClipper account</title>
              <meta
                name="description"
                content="Start a 14-day free trial. No credit card required."
              />
            </Helmet>
            <Form>
              <Field
                type="hidden"
                name="guestToken"
                value={values.guestToken}
              />

              {/* firstName + lastName removed from signup form (CRO punch list
                  2026-05-05). 159 backend usages of `user.firstName` already
                  default to "there" via `firstName || 'there'` so email
                  greetings still render. Names can be collected later in
                  onboarding if needed. */}

              <Row>
                <Col md="12">
                  <FormGroup>
                    <Form.Label>
                      <strong>Email</strong>
                    </Form.Label>
                    <div
                      onBlur={(e: React.FocusEvent<HTMLDivElement>) => {
                        const input = e.target as HTMLInputElement;
                        if (input.name === "email") {
                          setEmailSuggestion(suggestEmailDomain(input.value));
                        }
                      }}
                    >
                      <Field
                        placeholder="Email"
                        name="email"
                        type="email"
                        component={formikInput}
                      />
                    </div>
                    {emailSuggestion && (
                      <FormText className="text-muted">
                        Did you mean{" "}
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            const at = values.email.lastIndexOf("@");
                            const local = at !== -1 ? values.email.slice(0, at) : values.email;
                            setFieldValue("email", `${local}@${emailSuggestion}`);
                            setEmailSuggestion(null);
                          }}
                        >
                          {values.email.slice(0, values.email.lastIndexOf("@") + 1)}{emailSuggestion}
                        </a>
                        ?
                      </FormText>
                    )}
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md="12">
                  <FormGroup>
                    <Form.Label>
                      <strong>Password</strong>
                    </Form.Label>
                    <Field name="password">
                      {({ field, meta }) => (
                        <>
                          <InputGroup>
                            <Form.Control
                              {...field}
                              placeholder="Password"
                              type={showPassword ? "text" : "password"}
                              autoComplete="new-password"
                              isInvalid={meta.touched && !!meta.error}
                            />
                            <InputGroup.Append>
                              <BSButton
                                variant="outline-secondary"
                                type="button"
                                onClick={() => setShowPassword((v) => !v)}
                                aria-label={showPassword ? "Hide password" : "Show password"}
                                tabIndex={-1}
                              >
                                <FontAwesomeIcon icon={showPassword ? "eye-slash" : "eye"} />
                              </BSButton>
                            </InputGroup.Append>
                          </InputGroup>
                          <Form.Text className="text-muted">
                            At least 8 characters.
                          </Form.Text>
                          {meta.touched && meta.error && (
                            <div
                              className="invalid-feedback"
                              style={{ display: "block" }}
                            >
                              {meta.error}
                            </div>
                          )}
                        </>
                      )}
                    </Field>
                  </FormGroup>
                </Col>
              </Row>

              {/* <Row>
      <Col md="12">
        <FormGroup>
          <Form.Label><strong>Phone number</strong></Form.Label>
          <Field
            placeholder="Phone number"
            name="phoneNumber"
            component={formikInput}
          />
        </FormGroup>
      </Col>
    </Row> */}

              <Row style={{ display: "flex", justifyContent: "center" }}>
                <Btn
                  loading={loading}
                  className="btn--signup-b2c text-center"
                  onClick={() => {
                    sendGAEvent({ action: "signupB2C" });
                    capturePostHogEvent("signup_attempted", {
                      source: "SignupB2C",
                    });
                    onClick();
                  }}
                  variant="primary"
                  style={{
                    fontWeight: "bold",
                  }}
                >
                  {btnCta}
                </Btn>
              </Row>
              <Row
                className="justify-content-around px-5 mt-2"
                style={{
                  marginLeft: "auto",
                  marginRight: "auto",
                  maxWidth: "450px",
                }}
              >
                <div className="mx-2">
                  <FontAwesomeIcon icon="check" />
                  <span className="ml-1">14-day free trial</span>
                </div>
                <div className="mx-2">
                  <FontAwesomeIcon icon="check" />
                  <span className="ml-1">No credit card required</span>
                </div>
                <div className="mx-2">
                  <FontAwesomeIcon icon="check" />
                  <span className="ml-1">Cancel anytime</span>
                </div>
              </Row>
            </Form>
          </>
        );
      }}
    />
  );
};
type Values = {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
};

type Props = {
  btnCta?: string;
};

type ReduxProps = {
  createAlert: CreateAlertFn;
};

export const SignupB2C: React.SFC<Props & ReduxProps & RouteComponentProps> = ({
  history,
  location,
  btnCta,
  createAlert,
}) => {
  const [loading, setLoading] = React.useState(false);

  const {
    utm_source,
    utm_campaign: utm_campaign_param,
    utm_campign,
    utm_medium,
    utm_term,
    utm_content,
    logo,
    coupon,
    header,
    email,
  } = queryString.parse(location.search);
  // Accept both spellings; utm_campaign (correct) takes precedence over the old typo
  const utm_campaign = utm_campaign_param || utm_campign;

  React.useEffect(() => {
    capturePostHogEvent("signup_form_viewed", {
      source: "SignupB2C",
      utm_source: (utm_source as string) || "",
      utm_campaign: (utm_campaign as string) || "",
      utm_medium: (utm_medium as string) || "",
    });
  }, []);

  React.useEffect(() => {
    const { error } = queryString.parse(location.search);

    if (error) {
      const tokens = (error as string).split("\n");
      createAlert({
        type: "error",
        message: (
          <>
            <span>{error}</span>
            <br></br>
            <span>
              Email <b>support@docuclipper.com</b> to get help
            </span>
          </>
        ),
        timeout: 0,
      });
    }
  }, []);
  const onClick = ({
    email,
    password,
    firstName,
    lastName,
    companyName,
    jobTitle,
    guestToken,
  }) => {
    setLoading(true);
    doSignup({
      email: email,
      password,
      firstName,
      lastName,
      utm_source: (utm_source as string) || "",
      utm_campaign: (utm_campaign as string) || "",
      utm_medium: (utm_medium as string) || "",
      utm_term: (utm_term as string) || "",
      utm_content: (utm_content as string) || "",
      companyName,
      jobTitle,
      coupon: coupon as string,
      guestToken,
    })
      .then((rsp) => {
        trackSignup();
        sendGAEvent({
          action: "signupSuccess",
          category: "other",
          label: "",
        });
        capturePostHogEvent("signup_succeeded", {
          source: "SignupB2C",
          utm_source: (utm_source as string) || "",
          utm_campaign: (utm_campaign as string) || "",
          utm_medium: (utm_medium as string) || "",
        });
        setLoading(false);
        if (rsp.token) {
          logUserIn(rsp.token);
          history.push("/select-segment");
        }
        if (window && (window as any).lintrk) {
          (window as any).lintrk("track", { conversion_id: 10568564 });
        }
      })
      .catch((err) => {
        logErrorToGA(err);
        createAlert({
          message: err.message || "Error signing up. Please try again.",
          timeout: 0,
          type: "error",
        });
        sendGAEvent({
          action: "signupError",
          category: "other",
          label: "",
        });
        capturePostHogEvent("signup_failed", {
          source: "SignupB2C",
          errorMessage: err?.message || "",
        });
        setLoading(false);
      });
  };

  return (
    <div
      className="px-4"
      style={{
        marginLeft: "auto",
        marginRight: "auto",
        // marginTop: "4rem",
        maxWidth: "600px",
      }}
    >
      <Row>
        <Col md="12">
          <div className="mt-2">
            <Row>
              <Col>
                <Logo
                  fill="#3f6ad8"
                  stroke="3f6ad8"
                  style={{
                    display: "block",
                    margin: "auto",
                    height: "50px",
                    width: "120px",
                  }}
                />
              </Col>
              {logo && (
                <Col className="text-center">
                  <img alt="Logo" src={logo as string} height="40px" />
                </Col>
              )}
            </Row>
          </div>

          <div className="p-4 bg-white">
            <div className="text-center my-4">
              {header && (
                <Alert variant="success">
                  {((header as any) || "").split("\n").map((x) => (
                    <>
                      {x}
                      <br />
                    </>
                  ))}
                </Alert>
              )}
              <div className="h4 font-weight-bold">
                Let's create your account
              </div>
              <div>
                Already have an account? <a href="/login">Sign in</a>
              </div>
            </div>
            <div className="mt-2 mb-2">
              <AlertBanner />
            </div>
            <ContinueWithSocial
              goToFacebook={goToFacebook}
              goToGoogle={goToGoogle}
              goToMicrosoft={goToMicrosoft}
              label={null}
            />
            <div
              className="text-center text-muted my-3"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                fontSize: "0.85rem",
              }}
            >
              <hr style={{ flex: 1, margin: 0 }} />
              <span>or sign up with email</span>
              <hr style={{ flex: 1, margin: 0 }} />
            </div>
            <SignupFields
              onClick={onClick}
              btnCta={btnCta || "Start my free trial"}
              loading={loading}
              email={email as string}
            />
          </div>
        </Col>
      </Row>
    </div>
  );
};

SignupB2C.defaultProps = {
  btnCta: "Start my free trial",
};

const mapStateToProps = (state) => ({});

export default connect(mapStateToProps, {
  createAlert,
})(withRouter(SignupB2C));
